// import React, { useEffect, useRef, useState } from "react";
// import { useQuery } from "@tanstack/react-query";
// import axiosInstance from "../api/axiosInstance";

// export default function NotificationProvider() {
//   const previousRequestIdsRef = useRef(new Set());
//   const [initialized, setInitialized] = useState(false);

//   //  Load previous IDs once from localStorage
//   useEffect(() => {
//     try {
//       const storedIds = JSON.parse(localStorage.getItem("previousRequestIds")) || [];
//       previousRequestIdsRef.current = new Set(storedIds);
//       setInitialized(true);
//     } catch (error) {
//       console.log("Failed to load previous IDs:", error);
//     }
//   }, []);

//   const fetchActiveRequests = async () => {
//     try {
//       const hotelId = localStorage.getItem("hotelId");
//       const response = await axiosInstance.get(
//         `/api/v1/hotels/${hotelId}/active-requests`
//       );
//       return response.data.data;
//     } catch (error) {
//       if (error.response?.data?.message === "Booked rooms not found") {
//         return [];
//       }
//       throw error;
//     }
//   };

//   const { data: fetchData } = useQuery({
//     queryKey: ["activeRequests"],
//     queryFn: fetchActiveRequests,
//     refetchOnWindowFocus: true,
//     refetchInterval: 5000,
//   });

//   const playNotificationSound = async () => {
//     try {
//       const audio2 = new Audio("/notification.wav");
//       const audio1 = new Audio("/notification2.wav");

//       await audio1.play().catch((err) => console.warn("Autoplay blocked:", err));
      
//       // Wait for the first audio to finish
//       await new Promise((resolve) => {
//         audio1.addEventListener("ended", resolve);
//       });

//       await audio2.play().catch((err) => console.warn("Autoplay blocked:", err));
//     } catch (error) {
//       console.error("Sound Error:", error);
//     }
//   };


//   const showNotification = (title, options = {}) => {
//     try {
//       if (Notification.permission === "granted") {
//         new Notification(title, {
//           body: options.body || "You have a new request!",
//           icon: options.icon || "/logo.svg",
//         });
//       }
//     } catch (error) {
//       console.log("Notification Error:", error);
//     }
//   };

//   useEffect(() => {
//     if (!initialized || !Array.isArray(fetchData)) return;

//     try {
//       const currentIds = new Set(fetchData.map((req) => req.id));
//       const prevIds = previousRequestIdsRef.current;

//       //  Find truly new request IDs
//       const newRequests = fetchData.filter((req) => !prevIds.has(req.id));

//       if (newRequests.length > 0) {
//         playNotificationSound();
//         newRequests.forEach((request) => {
//           showNotification("New Check-in Request", {
//             body: request?.type || "New Request",
//             icon: "/logo.svg",
//           });
//         });

//         //  Update the ref + localStorage
//         const updatedIds = [...new Set([...prevIds, ...currentIds])];
//         previousRequestIdsRef.current = new Set(updatedIds);
//         localStorage.setItem("previousRequestIds", JSON.stringify(updatedIds));
//       }
//     } catch (error) {
//       console.log("Notification Check Error:", error);
//     }
//   }, [fetchData, initialized]);

//   return null;
// }


import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../api/axiosInstance";

export const ESCALATION_RANK = { l1: 1, l2: 2, stuck: 3 };

const BUZZER_SOUND = "/notification2.wav";
const BUZZER_REPEATS = 3;
const BUZZER_MAX_TONE_MS = 1500;

export default function NotificationProvider() {
  const previousRequestIdsRef = useRef(new Set());
  const escalatedLevelsRef = useRef({});
  const firstFetchDoneRef = useRef(false); //
  const audioUnlockedRef = useRef(false);
  const buzzingRef = useRef(false);
  const [initialized, setInitialized] = useState(false);

  const hotelId = localStorage.getItem("hotelId");

  useEffect(() => {
    try {
      const storedIds = JSON.parse(localStorage.getItem("previousRequestIds")) || [];
      previousRequestIdsRef.current = new Set(storedIds);
    } catch (error) {
      console.log("Failed to load previous IDs:", error);
    }

    try {
      const storedLevels = JSON.parse(localStorage.getItem("escalatedRequestLevels")) || {};
      escalatedLevelsRef.current = storedLevels;
    } catch (error) {
      console.log("Failed to load escalated levels:", error);
    }

    setInitialized(true);
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      if (audioUnlockedRef.current || !window.Audio) return;
      try {
        const primer = new Audio(BUZZER_SOUND);
        primer.muted = true;
        const played = primer.play();
        if (played && typeof played.then === "function") {
          played
            .then(() => {
              primer.pause();
              primer.currentTime = 0;
              audioUnlockedRef.current = true;
            })
            .catch(() => {});
        } else {
          audioUnlockedRef.current = true;
        }
      } catch (error) {
        console.log("Audio unlock error:", error);
      }
    };

    window.addEventListener("click", unlockAudio);
    window.addEventListener("keydown", unlockAudio);
    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const fetchActiveRequests = async () => {
    try {
      const currentHotelId = localStorage.getItem("hotelId");
      const response = await axiosInstance.get(`/api/v1/hotels/${currentHotelId}/active-requests`);
      return response.data.data;
    } catch (error) {
      if (error.response?.data?.message === "Booked rooms not found") {
        return [];
      }
      throw error;
    }
  };

  const { data: fetchData } = useQuery({
    queryKey: ["activeRequests", hotelId],
    queryFn: fetchActiveRequests,
    refetchOnWindowFocus: false,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

  const playNotificationSound = async () => {
    try {
      const audio2 = new Audio("/notification.wav");
      const audio1 = new Audio("/notification2.wav");
      await audio1.play().catch((err) => console.warn("Autoplay blocked:", err));
      await new Promise((resolve) => audio1.addEventListener("ended", resolve));
      await audio2.play().catch((err) => console.warn("Autoplay blocked:", err));
    } catch (error) {
      console.error("Sound Error:", error);
    }
  };

  const playEscalationBuzzer = async () => {
    if (buzzingRef.current) return;
    buzzingRef.current = true;

    try {
      for (let repeat = 0; repeat < BUZZER_REPEATS; repeat += 1) {
        const buzz = new Audio(BUZZER_SOUND);
        let blocked = false;
        await buzz.play().catch((err) => {
          blocked = true;
          console.warn("Autoplay blocked:", err);
        });
        if (blocked) break;
        await new Promise((resolve) => {
          const done = () => resolve();
          buzz.addEventListener("ended", done, { once: true });
          setTimeout(done, BUZZER_MAX_TONE_MS);
        });
      }
    } catch (error) {
      console.error("Buzzer Error:", error);
    } finally {
      buzzingRef.current = false;
    }
  };

  const showNotification = (title, options = {}) => {
    try {
      if (Notification.permission === "granted") {
        new Notification(title, {
          body: options.body || "You have a new request!",
          icon: options.icon || "/logo.svg",
        });
      }
    } catch (error) {
      console.log("Notification Error:", error);
    }
  };

  useEffect(() => {
    if (!initialized || !Array.isArray(fetchData)) return;

    try {
      const currentIds = new Set(fetchData.map((req) => req.id));
      const prevIds = previousRequestIdsRef.current;

      const levelsOf = (requests) =>
        requests.reduce((acc, req) => {
          const rank = ESCALATION_RANK[req.escalation_level];
          if (rank) acc[req.id] = rank;
          return acc;
        }, {});

      const persistLevels = (levels) => {
        escalatedLevelsRef.current = levels;
        localStorage.setItem("escalatedRequestLevels", JSON.stringify(levels));
      };

      // Skip first fetch after login
      if (!firstFetchDoneRef.current) {
        previousRequestIdsRef.current = currentIds;
        localStorage.setItem("previousRequestIds", JSON.stringify([...currentIds]));
        persistLevels(levelsOf(fetchData));
        firstFetchDoneRef.current = true; //  Mark first fetch handled
        return; // Prevent any notifications
      }

      const knownLevels = escalatedLevelsRef.current;
      const nextLevels = {};
      const newlyEscalated = [];

      fetchData.forEach((req) => {
        const rank = ESCALATION_RANK[req.escalation_level];
        if (!rank) return;
        nextLevels[req.id] = rank;
        if (rank > (knownLevels[req.id] || 0)) {
          newlyEscalated.push(req);
        }
      });

      persistLevels(nextLevels);

      if (newlyEscalated.length > 0) {
        playEscalationBuzzer();
        newlyEscalated.forEach((request) => {
          showNotification("Guest request escalated", {
            body: `${request?.type || "Request"}${
              request?.room_number ? ` · Room ${request.room_number}` : ""
            } is waiting for action`,
            icon: "/logo.svg",
          });
        });
      }

      // Find truly new requests after the first fetch
      const newRequests = fetchData.filter((req) => !prevIds.has(req.id));

      if (newRequests.length > 0) {
        playNotificationSound();
        newRequests.forEach((request) => {
          showNotification("New Check-in Request", {
            body: request?.type || "New Request",
            icon: "/logo.svg",
          });
        });

        const updatedIds = [...new Set([...prevIds, ...currentIds])];
        previousRequestIdsRef.current = new Set(updatedIds);
        localStorage.setItem("previousRequestIds", JSON.stringify(updatedIds));
      }
    } catch (error) {
      console.log("Notification Check Error:", error);
    }
  }, [fetchData, initialized]);

  return null;
}
